import ImageUpload from "@/components/ImageUpload";
import BindingSlugPicker from "@/components/BindingSlugPicker";
import { CONTENT } from "@/constants/contentTestIds";

const TYPES = [
  { value: "text", label: "Text (short)" },
  { value: "long_text", label: "Text (paragraph)" },
  { value: "integer", label: "Integer" },
  { value: "boolean", label: "Yes/No" },
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
  { value: "date", label: "Date" },
  { value: "image", label: "Image" },
  { value: "select", label: "Select (one of)" },
  { value: "binding_slug", label: "Brand Kit binding" },
];

/**
 * Form generator. Renders one input per FieldDefinition. Pure controlled
 * component — owner manages `values`.
 */
export default function DynamicFieldForm({ fields, values, onChange, brandId, testidPrefix }) {
  const setField = (key, v) => onChange({ ...values, [key]: v });
  const sorted = [...(fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-5">
      {sorted.map((f) => (
        <Field key={f.field_id || f.key} field={f}>
          <Input
            field={f}
            value={values?.[f.key]}
            onChange={(v) => setField(f.key, v)}
            brandId={brandId}
            testid={`${testidPrefix}${f.key}`}
          />
        </Field>
      ))}
      {sorted.length === 0 && (
        <p className="text-sm text-[#6B6358]">No fields defined yet.</p>
      )}
    </div>
  );
}

function Field({ field, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-1.5">
        {field.name}
        {field.required && <span className="text-[#B5462F] ml-1">*</span>}
        <span className="ml-2 normal-case tracking-normal text-[#6B6358] italic font-mono text-[10px]">
          {field.type}
        </span>
      </label>
      {children}
      {field.help_text && (
        <p className="text-xs text-[#6B6358] mt-1 italic">{field.help_text}</p>
      )}
    </div>
  );
}

function Input({ field, value, onChange, brandId, testid }) {
  const common = "w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]";
  switch (field.type) {
    case "long_text":
      return <textarea data-testid={testid} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
    case "integer":
      return <input data-testid={testid} type="number" step="1" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))} className={common} />;
    case "boolean":
      return (
        <label className="inline-flex items-center gap-2">
          <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          <span className="text-sm">{value ? "Yes" : "No"}</span>
        </label>
      );
    case "url":
    case "email":
      return <input data-testid={testid} type={field.type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
    case "date":
      return <input data-testid={testid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
    case "image":
      return <ImageUpload value={value || ""} onChange={onChange} brandId={brandId} testIdSuffix={testid} />;
    case "binding_slug":
      return (
        <BindingSlugPicker
          value={value || ""}
          onChange={onChange}
          brandId={brandId}
          assetClass={field.binding_asset_class || "logo"}
          testid={testid}
        />
      );
    case "select":
      return (
        <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common}>
          <option value="">— select —</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    default:
      return <input data-testid={testid} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} />;
  }
}

export { TYPES as FIELD_TYPES };
